import { Link } from "@tanstack/react-router";
import { Arrow } from "../components/Brand";
import { PageMeta } from "../components/PageMeta";
import { ProjectMedia } from "../components/ProjectMedia";
import { CTASection, SectionHeading, TagList } from "../components/Sections";
import { SystemVisual } from "../components/SystemVisual";
import { clientProjects, processSteps } from "../data/site";
import { useI18n, useLocalizedContent } from "../i18n";

const content = {
  zh: {
    meta: "AI 原生产品工程",
    hero: "把想法，快速做成可以工作的产品。",
    lead: "AccSci 从产品定义和快速原型开始，构建 AI 软件、SaaS、智能硬件与软硬一体产品，并陪伴它们走向发布、量产和长期运行。",
    primary: "告诉我们你的想法",
    secondary: "查看真实案例",
    journey: [
      "Idea",
      "Working Prototype",
      "Product Engineering",
      "Pilot / Launch",
      "Scale",
      "Operate",
    ],
    intro: [
      "从想法开始",
      "先用一个可以工作的原型，回答最关键的问题。",
      "我们不要求你先写完整需求。一个目标、一张草图或一个业务问题，就足以开始。",
    ],
    problems: [
      ["01", "想法还不够清晰", "把模糊概念变成可以验证的产品假设。"],
      [
        "02",
        "需要尽快看到结果",
        "先交付可操作的软件原型、实验室样机，或经验证的视频和图片演示。",
      ],
      [
        "03",
        "下一步工程路径未知",
        "同时识别模型、软件、电子、结构、供应链和运维边界，给出走向产品的现实路线。",
      ],
    ],
    inDays: [
      "关于 IN DAYS",
      "“In days”指可验证的工作原型：在线软件、可操作的 SaaS 流程、实验室样机，或经验证的视频与图片演示。生产级工程、认证和量产在后续阶段完成。",
    ],
    serviceHead: [
      "三种合作方式",
      "无论是纯软件，还是进入现实世界的设备。",
      "按产品所处阶段合作，也可以从原型一路持续到规模化运行。",
    ],
    services: [
      {
        index: "01",
        title: "Prototype Sprint",
        scenario: "有想法、需要快速判断方向",
        flow: ["定义问题", "搭建原型", "快速演示", "验证下一步"],
        change: "在数天内获得可以操作、可以演示、可以用于决策的工作原型。",
        tags: ["AI / SaaS 原型", "交互 Demo", "实验室样机", "验证报告"],
      },
      {
        index: "02",
        title: "Product Build",
        scenario: "方向已验证、需要正式研发和发布",
        flow: ["产品定义", "软件与硬件工程", "系统验证", "Pilot / Launch"],
        change: "把原型扩展成可靠、安全、可维护并能交付给真实用户的产品。",
        tags: ["AI 软件 / SaaS", "云与设备服务", "智能硬件", "软硬件系统"],
      },
      {
        index: "03",
        title: "Scale & Operate",
        scenario: "产品已上线或进入试产、需要稳定扩展",
        flow: ["工程优化", "测试认证", "发布或量产", "云端与维护"],
        change: "让软件可靠扩容，让硬件稳定生产，让整个产品持续运行和演进。",
        tags: ["性能与成本优化", "量产导入", "云端运维", "持续迭代"],
      },
    ],
    labels: ["适合阶段", "带来结果", "交付内容", "交付流程"],
    allServices: "查看完整服务能力",
    spectrum: "AI 软件 × SaaS × 智能硬件 × AIoT",
    example: [
      "工程示例",
      "一个现场问题，如何变成可执行的产品能力。",
      "模型只是其中一个节点。可靠产品还需要数据、权限、软件工具、边缘设备、人工确认和反馈闭环。",
      "示例任务 · ACC-042",
    ],
    trace: [
      [
        "01",
        "捕获现场",
        "通过图片、语音与传感器数据描述真实问题。",
        "IMAGE · VOICE · SENSOR",
      ],
      [
        "02",
        "建立上下文",
        "调取业务知识、历史记录、当前状态与操作者权限。",
        "RAG · PERMISSION · MEMORY",
      ],
      [
        "03",
        "形成方案",
        "AI 判断问题、生成步骤并标记风险与不确定项。",
        "VLM · AGENT · POLICY",
      ],
      [
        "04",
        "执行任务",
        "经确认后调用软件工具或控制边缘设备完成动作。",
        "TOOLS · APPROVAL · EDGE",
      ],
      [
        "05",
        "验证结果",
        "收集运行结果与人员反馈，持续改进系统。",
        "TRACE · EVAL · LEARNING",
      ],
    ],
    work: ["真实交付", "不只展示概念，也展示已经上线的项目。"],
    proof: ["问题", "系统", "结果"],
    process: ["合作路径", "从快速验证，到长期运行。"],
  },
  en: {
    meta: "AI-Native Product Engineering",
    hero: "Turn ideas into working products—fast.",
    lead: "AccSci starts with product definition and rapid prototyping, then builds AI software, SaaS, smart hardware, and integrated products through launch, production, and long-term operation.",
    primary: "Share your idea",
    secondary: "See real work",
    journey: [
      "Idea",
      "Working Prototype",
      "Product Engineering",
      "Pilot / Launch",
      "Scale",
      "Operate",
    ],
    intro: [
      "Start with the idea",
      "Use a working prototype to answer the most important question first.",
      "You do not need a finished specification. A goal, sketch, or business problem is enough to start.",
    ],
    problems: [
      [
        "01",
        "The idea is still fuzzy",
        "Turn a loose concept into a testable product hypothesis.",
      ],
      [
        "02",
        "You need to see it quickly",
        "Start with operable software, a lab prototype, or a validated video and image demonstration.",
      ],
      [
        "03",
        "The engineering path is unclear",
        "Identify model, software, electronics, mechanical, supply-chain, and operations constraints, then map the path to product.",
      ],
    ],
    inDays: [
      "WHAT IN DAYS MEANS",
      "In days refers to a testable working prototype: live software, an operable SaaS flow, a lab prototype, or a validated video and image demonstration. Production engineering, certification, and mass production follow later.",
    ],
    serviceHead: [
      "Three ways to work together",
      "From pure software to devices in the real world.",
      "Engage at the stage your product is in, or stay with us from prototype through operation at scale.",
    ],
    services: [
      {
        index: "01",
        title: "Prototype Sprint",
        scenario: "An idea that needs fast validation",
        flow: ["Define", "Prototype", "Demonstrate", "Decide"],
        change:
          "Get an operable, demonstrable working prototype for decision-making within days.",
        tags: [
          "AI / SaaS prototype",
          "Interactive demo",
          "Lab prototype",
          "Validation brief",
        ],
      },
      {
        index: "02",
        title: "Product Build",
        scenario: "A validated direction that needs engineering and launch",
        flow: [
          "Product definition",
          "Engineering",
          "System validation",
          "Pilot / Launch",
        ],
        change:
          "Turn the prototype into a reliable, secure, maintainable product for real users.",
        tags: [
          "AI software / SaaS",
          "Cloud & device services",
          "Smart hardware",
          "Integrated systems",
        ],
      },
      {
        index: "03",
        title: "Scale & Operate",
        scenario:
          "A launched or pilot-stage product that needs dependable growth",
        flow: ["Optimize", "Test & certify", "Launch / produce", "Operate"],
        change:
          "Scale software reliably, manufacture hardware consistently, and keep the whole product evolving.",
        tags: [
          "Performance & cost",
          "Production ramp",
          "Cloud operations",
          "Continuous iteration",
        ],
      },
    ],
    labels: ["Best for", "Outcome", "Deliverables", "Delivery flow"],
    allServices: "Explore all services",
    spectrum: "AI Software × SaaS × Smart Hardware × AIoT",
    example: [
      "Engineering example",
      "How a field problem becomes an executable product capability.",
      "The model is only one node. A reliable product also needs data, permissions, software tools, edge devices, human approval, and a feedback loop.",
      "EXAMPLE TRACE · ACC-042",
    ],
    trace: [
      [
        "01",
        "Capture the field",
        "Describe the real problem with images, voice, and sensor data.",
        "IMAGE · VOICE · SENSOR",
      ],
      [
        "02",
        "Build context",
        "Retrieve business knowledge, history, current state, and operator permissions.",
        "RAG · PERMISSION · MEMORY",
      ],
      [
        "03",
        "Form a plan",
        "AI identifies the issue, proposes steps, and marks risks and uncertainty.",
        "VLM · AGENT · POLICY",
      ],
      [
        "04",
        "Execute",
        "After approval, call software tools or control an edge device to act.",
        "TOOLS · APPROVAL · EDGE",
      ],
      [
        "05",
        "Verify",
        "Collect outcomes and human feedback to keep improving the system.",
        "TRACE · EVAL · LEARNING",
      ],
    ],
    work: ["Delivered work", "Not just concepts—products already launched."],
    proof: ["Problem", "System", "Outcome"],
    process: [
      "Engagement path",
      "From rapid validation to long-term operation.",
    ],
  },
} as const;

export function HomePage() {
  const { locale } = useI18n();
  const c = content[locale];
  const projects = useLocalizedContent(clientProjects);
  const steps = useLocalizedContent(processSteps);
  return (
    <>
      <PageMeta title={c.meta} />
      <section className="home-hero">
        <div className="container home-name-origin">
          <p className="home-name-origin__source" lang="en">
            <mark>Acc</mark>elerating <mark>Sci</mark>ence
          </p>
          <p className="hero-kicker">AI-NATIVE PRODUCT ENGINEERING</p>
        </div>
        <div className="container home-hero__grid">
          <div className="home-hero__copy">
            <h1>
              {locale === "zh" ? (
                <>
                  把想法，
                  <br />
                  快速做成可以
                  <br />
                  工作的产品。
                </>
              ) : (
                c.hero
              )}
            </h1>
            <div className="hero-promise">
              <strong>From idea to a working prototype in days.</strong>
              <strong>From prototype to production at scale.</strong>
            </div>
            <p>{c.lead}</p>
            <div className="button-row">
              <Link className="button" to="/contact">
                {c.primary} <Arrow />
              </Link>
              <Link className="text-link" to="/work">
                {c.secondary} <Arrow direction="up-right" />
              </Link>
            </div>
          </div>
          <SystemVisual />
        </div>
        <ol className="container chain-strip" aria-label="Product journey">
          {c.journey.map((item, i) => (
            <li key={item}>
              {item}
              {i < 5 && <Arrow />}
            </li>
          ))}
        </ol>
      </section>
      <section className="section problem-section">
        <div className="container">
          <SectionHeading
            eyebrow={c.intro[0]}
            title={c.intro[1]}
            description={c.intro[2]}
          />
          <div className="problem-grid">
            {c.problems.map(([n, title, text]) => (
              <article key={n}>
                <span>{n}</span>
                <div className="problem-grid__content">
                  <h3>{title}</h3>
                  <p>{text}</p>
                </div>
              </article>
            ))}
          </div>
          <div className="problem-conclusion">
            <span>{c.inDays[0]}</span>
            <p>{c.inDays[1]}</p>
          </div>
        </div>
      </section>
      <section className="section solution-section">
        <div className="container">
          <SectionHeading
            eyebrow={c.serviceHead[0]}
            title={c.serviceHead[1]}
            description={c.serviceHead[2]}
          />
          <div className="solution-list">
            {c.services.map((s) => (
              <article className="solution-story" key={s.index}>
                <div className="solution-story__title">
                  <span>{s.index}</span>
                  <h3>{s.title}</h3>
                </div>
                <div className="solution-story__detail">
                  <div>
                    <span>{c.labels[0]}</span>
                    <p>{s.scenario}</p>
                  </div>
                  <ol
                    className="solution-flow"
                    aria-label={`${s.title} ${c.labels[3]}`}
                  >
                    {s.flow.map((step, i) => (
                      <li key={step}>
                        {step}
                        {i < s.flow.length - 1 && <Arrow />}
                      </li>
                    ))}
                  </ol>
                  <div>
                    <span>{c.labels[1]}</span>
                    <strong>{s.change}</strong>
                  </div>
                  <div>
                    <span>{c.labels[2]}</span>
                    <TagList tags={[...s.tags]} />
                  </div>
                </div>
              </article>
            ))}
          </div>
          <div className="section-end-link">
            <span>{c.spectrum}</span>
            <Link className="text-link" to="/capabilities">
              {c.allServices} <Arrow />
            </Link>
          </div>
        </div>
      </section>
      <section className="section dark-section walkthrough-section">
        <div className="container walkthrough-section__grid">
          <div className="walkthrough-sticky">
            <p className="section-label section-label--inverse">
              {c.example[0]}
            </p>
            <h2>{c.example[1]}</h2>
            <p>{c.example[2]}</p>
            <div className="trace-status">
              <i /> {c.example[3]}
            </div>
          </div>
          <div className="task-trace">
            {c.trace.map(([n, title, text, tech]) => (
              <article key={n}>
                <span>{n}</span>
                <h3>{title}</h3>
                <p>{text}</p>
                <small>{tech}</small>
              </article>
            ))}
          </div>
        </div>
      </section>
      <section className="section work-preview">
        <div className="container">
          <SectionHeading eyebrow={c.work[0]} title={c.work[1]} />
          <div className="project-list">
            {projects.slice(0, 2).map((p) => (
              <Link
                key={p.slug}
                to="/work/$projectId"
                params={{ projectId: p.slug }}
                className="project-row"
              >
                <div className={`project-visual project-visual--${p.accent}`}>
                  <ProjectMedia project={p} />
                </div>
                <div className="project-row__info">
                  <span className="micro-label">{p.industry}</span>
                  <h3>{p.title}</h3>
                  <p>{p.summary}</p>
                  <dl className="project-proof">
                    {p.proof.map((v, i) => (
                      <div key={v}>
                        <dt>{c.proof[i]}</dt>
                        <dd>{v}</dd>
                      </div>
                    ))}
                  </dl>
                  <TagList tags={p.tags} />
                </div>
                <div className="round-arrow">
                  <Arrow direction="up-right" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
      <section className="section process-section">
        <div className="container">
          <SectionHeading eyebrow={c.process[0]} title={c.process[1]} />
          <div className="process-grid">
            {steps.map((s) => (
              <article key={s.index}>
                <span>{s.index}</span>
                <h3>{s.title}</h3>
                <p>{s.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>
      <CTASection />
    </>
  );
}
